import React from "react";
import {ManuscriptModel} from "../../models/manuscript.model";
import Grid from "@material-ui/core/Grid";
import {StyledButton} from "../ui-base/button";
import {CustomBookMapIcon} from "../ui-base/icons/custom-book-map-icon";
import {CustomFeatherIcon} from "../ui-base/icons/custom-feather-icon";
import {CustomMapPinIcon} from "../ui-base/icons/custom-map-pin-icon";
import {CustomGlobeStandIcon} from "../ui-base/icons/custom-globe-stand-icon";
import {CustomHourglassIcon} from "../ui-base/icons/custom-hourglass-icon";
import {CustomPlusCircleIcon} from "../ui-base/icons/custom-plus-cicrle-icon";
import {CustomClockIcon} from "../ui-base/icons/custom-clock-icon";
import {CustomSexIcon} from "../ui-base/icons/custom-sex-icon";
import styled from "styled-components";
import {Colors} from "../../theme/colors";
import {ApplicationStatusEnum} from "../../models/application-status.enum";
import {PublisherApplicationModel} from "../../models/publisher/publisher-application.model";
import {ApplicationService} from "../../services/application.service";
import {CustomSmallLinkIcon} from "../ui-base/icons/custom-small-link-icon";
import {StyledSmallButton} from "../ui-base/small-button";
import {showSnackSuccess} from "../../store/global/global-slice";
import {useDispatch, useSelector} from "react-redux";
import {NotAccessModal} from "../ui-base/not-access-modal";
import {AuthMeModel} from "../../models/auth-me.model";
import {RootState} from "../../store/root-reducer";
import {AccountTypeEnum} from "../../models/account-type.enum";
import {NoteModal} from "./note-modal";
import Lightbox from "react-image-lightbox";
import {useTranslation} from "react-i18next";
import {BookCover} from "./book-cover";

export const LeftBookDetails: React.FC<Props> = ({manuscript, application, updateStatus, author}) => {
    const [openNotAccess, setOpenNotAccess] = React.useState<boolean>(false);
    const [openNoteModal, setOpenNoteModal] = React.useState<boolean>(false);
    const [openCoverLightbox, setOpenCoverLightbox] = React.useState<boolean>(false);
    const [openIllustrationLightbox, setOpenIllustrationLightbox] = React.useState<boolean>(false);
    const dispatch = useDispatch();
    const {t} = useTranslation(['author', 'common']);

    const authMe: AuthMeModel = useSelector(
        (state: RootState) => state.auth.user
    );

    const contentDownloaded = async () => {
        if (
            application &&
            (application?.status === ApplicationStatusEnum.OpenByPub)
        ) {
            const res = await ApplicationService.contentDownloaded(application.id);

            if (res.status) {
                updateStatus(res.data.status);
            }
        }
    }

    const reportDownloaded = async (event) => {
        if (authMe.account_type === AccountTypeEnum.PUB) {
            const allowed = authMe.subscription.features.find((feature) => feature === 'virtual-bookmap');

            if (!allowed) {
                event.preventDefault();
                setOpenNotAccess(true);
            } else {
                updateStatusReportDownloaded();
            }
        } else {
            updateStatusReportDownloaded();
        }
    }

    const updateStatusReportDownloaded = async () => {
        if (
            application &&
            (application.status === ApplicationStatusEnum.OpenByPub)
        ) {
            const res = await ApplicationService.reportDownloaded(application.id);

            if (res.status) {
                updateStatus(res.data.status);
            }
        }
    }

    const showNote = () => {
        setOpenNoteModal(true);
    }

    const getMapUrl = () => {
        return process.env.REACT_APP_API.replace('api/', '') + `manuscript/${manuscript.id}/virtual-bookmap/pdf`;
        // if (application) {
        //     return process.env.REACT_APP_API.replace('api/', '') + `report_for_publisher/?app_id=${application?.id}&usr_id=${application.publisher}`;
        // } else {
        //     return process.env.REACT_APP_API.replace('api/', '') + `report_for_author/?man_id=${manuscript.id}&usr_id=${manuscript.author}`;
        // }
    }

    const getViewUrl = () => {
        const baseUrl = process.env.REACT_APP_API.replace('api/', '');
        return `${baseUrl}manuscript/${manuscript.id}/virtual-bookmap/start?language=${authMe.language}`;
    };

    const copyLink = () => {
        let temp = document.createElement('textarea');
        temp.value = window.location.origin + '/book-view/' + manuscript.id;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);

        dispatch(showSnackSuccess(t('leftManuscriptDetails.linkCopied')));
    }

    const getNarration = () => {
        const typeOfNarration = manuscript.tags.find((tag) => tag.tag_type === 'typeOfNarration');

        if (typeOfNarration && typeOfNarration.name === '1' || typeOfNarration.name === '2' || typeOfNarration.name === '3') {
            return t('addBook.form.typeOfNarration' + typeOfNarration.name);
        } else if (typeOfNarration) {
            return typeOfNarration.name;
        } else {
            return '---';
        }
    }

    return (
        <>
            <Grid item style={{position: 'relative', flex: 0, minWidth: '230px'}}>
                <BookCover
                    title={manuscript?.title}
                    description={manuscript?.description}
                    author={author}
                />

                <Grid item container direction={"column"} spacing={2}>

                    {manuscript.content_full?.file &&
                        <Grid item>
                            <a href={manuscript.content_full.file} target="_blank" onClick={contentDownloaded}
                               rel="noreferrer">
                                <StyledButton type={"button"}
                                              color={"transparentViolet"}>{t('leftManuscriptDetails.downloadManuscript')}</StyledButton>
                            </a>
                        </Grid>
                    }

                    {manuscript.content_fragment?.file &&
                        <Grid item>
                            <a href={manuscript.content_fragment.file} target="_blank" onClick={contentDownloaded}
                               rel="noreferrer">
                                <StyledButton type={"button"}
                                              color={"transparentViolet"}>{t('leftManuscriptDetails.downloadPart')}</StyledButton>
                            </a>
                        </Grid>
                    }

                    {manuscript.cover?.file &&
                        <Grid item>
                            <StyledDetailsHeader>{t('common:cover')}:</StyledDetailsHeader>
                            <img src={manuscript.cover.file} onClick={() => setOpenCoverLightbox(true)}
                                 style={{cursor: 'pointer', marginTop: '0.5rem'}} alt={'Cover'}/>
                            {openCoverLightbox &&
                                <Lightbox mainSrc={manuscript.cover.file}
                                          onCloseRequest={() => setOpenCoverLightbox(false)}/>
                            }
                        </Grid>
                    }

                    {manuscript.illustration?.file &&
                        <Grid item>
                            <StyledDetailsHeader>{t('common:illustration')}:</StyledDetailsHeader>
                            <img src={manuscript.illustration.file} onClick={() => setOpenIllustrationLightbox(true)}
                                 style={{cursor: 'pointer', marginTop: '0.5rem'}} alt={'Illustration'}/>
                            {openIllustrationLightbox &&
                                <Lightbox mainSrc={manuscript.illustration.file}
                                          onCloseRequest={() => setOpenIllustrationLightbox(false)}/>
                            }
                        </Grid>
                    }

                    {manuscript.is_note_accepted &&
                        <Grid item>
                            <StyledButton type={"button"} color={"transparentViolet"}
                                          onClick={showNote}>{t('leftManuscriptDetails.showNote')}</StyledButton>
                        </Grid>
                    }

                    {manuscript?.is_bookmap_ready && authMe.account_type === AccountTypeEnum.PUB &&
                        <StyledDownloadBookMapContainer item container direction={"row"}>
                            <a href={getViewUrl()}
                               target="_blank" rel="noreferrer" onClick={reportDownloaded}>
                                <Grid item container direction={"row"}>
                                    <Grid item>
                                        <CustomBookMapIcon style={{height: '49px', width: '49px', fill: 'none'}}/>
                                    </Grid>
                                    <Grid item style={{display: 'flex'}}>
                                        <StyledDownloadBookMap>{t('leftManuscriptDetails.showBookmap')}</StyledDownloadBookMap>
                                    </Grid>
                                </Grid>
                            </a>
                        </StyledDownloadBookMapContainer>
                    }

                    <StyledHr/>

                    <Grid item>
                        <StyledDetailsHeader>{t('leftManuscriptDetails.cardWithoutLogin')}</StyledDetailsHeader>
                    </Grid>

                    <Grid item>
                        <a href={window.location.origin + '/book-view/' + manuscript.id} target="_blank">
                            <StyledSmallButton type={"button"} color={"transparentViolet"}>
                                {t('common:preview')}
                            </StyledSmallButton>
                        </a>
                    </Grid>

                    <Grid item>
                        <StyledSmallButton type={"button"} color={"transparentViolet"} onClick={() => {
                            copyLink();
                        }}>
                            <CustomSmallLinkIcon
                                style={{fontSize: '0.9rem', marginRight: '10px'}}/> {t('common:copyLink')}
                        </StyledSmallButton>
                    </Grid>

                    {(manuscript.tags.find((tag) => tag.tag_type === 'typeOfNarration') || manuscript.tags.find((tag) => tag.tag_type === 'realLocation') || manuscript.tags.find((tag) => tag.tag_type === 'fictionLocation') || manuscript.tags.find((tag) => tag.tag_type === 'actionTime') || manuscript.tags.find((tag) => tag.tag_type === 'readerAddInfo')) &&
                        <>
                            <StyledHr/>

                            <Grid item>
                                <StyledDetailsHeader>{t('leftManuscriptDetails.characteristic')}</StyledDetailsHeader>
                            </Grid>

                            {manuscript.tags.find((tag) => tag.tag_type === 'typeOfNarration') &&
                                <Grid item container direction={"column"}>
                                    <Grid item container direction={"row"}>
                                        <Grid item>
                                            <CustomFeatherIcon style={{fill: 'none'}}/>
                                        </Grid>
                                        <Grid>
                                            <StyledDetailsSpan>{t('leftManuscriptDetails.typeOfNarration')}:</StyledDetailsSpan>
                                        </Grid>
                                    </Grid>

                                    <Grid item>
                                        <StyledDetailsP>{getNarration()}</StyledDetailsP>
                                    </Grid>
                                </Grid>
                            }

                            {manuscript.tags.find((tag) => tag.tag_type === 'realLocation') &&
                                <Grid item container direction={"column"}>
                                    <Grid item container direction={"row"}>
                                        <Grid item>
                                            <CustomMapPinIcon style={{fill: 'none'}}/>
                                        </Grid>
                                        <Grid>
                                            <StyledDetailsSpan>{t('leftManuscriptDetails.realLocation')}:</StyledDetailsSpan>
                                        </Grid>
                                    </Grid>

                                    <Grid item>
                                        <StyledDetailsP>{manuscript.tags.find((tag) => tag.tag_type === 'realLocation')?.name || '---'}</StyledDetailsP>
                                    </Grid>
                                </Grid>
                            }

                            {manuscript.tags.find((tag) => tag.tag_type === 'fictionLocation') &&
                                <Grid item container direction={"column"}>
                                    <Grid item container direction={"row"}>
                                        <Grid item>
                                            <CustomGlobeStandIcon style={{fill: 'none'}}/>
                                        </Grid>
                                        <Grid>
                                            <StyledDetailsSpan>{t('leftManuscriptDetails.fictionLocation')}:</StyledDetailsSpan>
                                        </Grid>
                                    </Grid>

                                    <Grid item>
                                        <StyledDetailsP>{manuscript.tags.find((tag) => tag.tag_type === 'fictionLocation')?.name || '---'}</StyledDetailsP>
                                    </Grid>
                                </Grid>
                            }

                            {manuscript.tags.find((tag) => tag.tag_type === 'actionTime') &&
                                <Grid item container direction={"column"}>
                                    <Grid item container direction={"row"}>
                                        <Grid item>
                                            <CustomHourglassIcon style={{fill: 'none'}}/>
                                        </Grid>
                                        <Grid>
                                            <StyledDetailsSpan>{t('leftManuscriptDetails.actionTime')}:</StyledDetailsSpan>
                                        </Grid>
                                    </Grid>

                                    <Grid item>
                                        <StyledDetailsP>{manuscript.tags.find((tag) => tag.tag_type === 'actionTime')?.name || '---'}</StyledDetailsP>
                                    </Grid>
                                </Grid>
                            }
                        </>
                    }

                    {(manuscript.tags.find((tag) => tag.tag_type === 'readerGender') || manuscript.tags.find((tag) => tag.tag_type === 'readerAge')) &&
                        <>
                            <StyledHr/>

                            <Grid item>
                                <StyledDetailsHeader>{t('leftManuscriptDetails.characteristic2')}</StyledDetailsHeader>
                            </Grid>

                            {manuscript.tags.find((tag) => tag.tag_type === 'readerAge') &&
                                <Grid item container direction={"column"}>
                                    <Grid item container direction={"row"}>
                                        <Grid item>
                                            <CustomClockIcon style={{fill: 'none'}}/>
                                        </Grid>
                                        <Grid>
                                            <StyledDetailsSpan>{t('common:age')}:</StyledDetailsSpan>
                                        </Grid>
                                    </Grid>

                                    <Grid item>
                                        <StyledDetailsP>{manuscript.tags.find((tag) => tag.tag_type === 'readerAge')?.name || '---'}</StyledDetailsP>
                                    </Grid>
                                </Grid>
                            }

                            {manuscript.tags.find((tag) => tag.tag_type === 'readerGender') &&
                                <Grid item container direction={"column"}>
                                    <Grid item container direction={"row"}>
                                        <Grid item>
                                            <CustomSexIcon style={{fill: 'none'}}/>
                                        </Grid>
                                        <Grid>
                                            <StyledDetailsSpan>{t('common:sex')}:</StyledDetailsSpan>
                                        </Grid>
                                    </Grid>

                                    <Grid item>
                                        <StyledDetailsP>{manuscript.tags.find((tag) => tag.tag_type === 'readerGender')?.name || '---'}</StyledDetailsP>
                                    </Grid>
                                </Grid>
                            }

                            {manuscript.tags.find((tag) => tag.tag_type === 'readerAddInfo') &&
                                <Grid item container direction={"column"}>
                                    <Grid item container direction={"row"}>
                                        <Grid item>
                                            <CustomPlusCircleIcon style={{fill: 'none'}}/>
                                        </Grid>
                                        <Grid>
                                            <StyledDetailsSpan>{t('leftManuscriptDetails.addInfo')}:</StyledDetailsSpan>
                                        </Grid>
                                    </Grid>

                                    <Grid item>
                                        <StyledDetailsP>{manuscript.tags.find((tag) => tag.tag_type === 'readerAddInfo')?.name || '---'}</StyledDetailsP>
                                    </Grid>
                                </Grid>
                            }
                        </>
                    }
                </Grid>
            </Grid>

            <NotAccessModal open={openNotAccess} setOpen={setOpenNotAccess}/>

            <NoteModal open={openNoteModal} setOpen={setOpenNoteModal} id={manuscript.id}/>
        </>
    );
}

interface Props {
    manuscript: ManuscriptModel;
    application?: PublisherApplicationModel;
    updateStatus?: any;
    author?: string;
}

const StyledDownloadBookMapContainer = styled(Grid)`
  cursor: pointer;
`;

const StyledDownloadBookMap = styled('p')`
  color: ${Colors.pink};
  padding: 0 5px;
  font-weight: 500;
  align-self: center;
`;

const StyledHr = styled('hr')`
  background-color: #E0E0E0;
  border: 0;
  height: 1px;
  width: 80%;
  margin: 1rem auto;
`;

const StyledDetailsHeader = styled('p')`
  color: ${Colors.violet};
  font-weight: 500;
`;

const StyledDetailsSpan = styled('span')`
  color: ${Colors.grayText};
  font-weight: 400;
  margin-left: 5px;
  font-size: 0.9rem;
`;

const StyledDetailsP = styled('p')`
  font-weight: 400;
  font-size: 0.9rem;
`;

const StyledBookLink = styled('p')`
  font-size: 0.8rem;
  font-weight: 500;
  color: #0B5CD7;
  background-color: #fff;
`;
